import React from "react";
import withStyles from "isomorphic-style-loader/lib/withStyles";
import { withRouter } from "react-router-dom";
import moment from "moment";
import ReactTable from "react-table";
import { Button, Col, Container, Form, Row } from "reactstrap";
import { faFileAlt, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import BaseInput from "../../../toolkit/baseInput";
import BaseButtonWithIconAndTooltip from "../../../toolkit/baseButtonWithIconAndTooltip";
import DatePicker from "../common/DatePicker";
import MexicanCurrency from "../common/currency";
import {
  downloadPaymentComplementReportCsv,
  openPaymentComplementReportXml
} from "../../actions/paymentComplementReport";
import {
  PaymentComplementReportFilters,
  PaymentComplementReportRow
} from "../../../types/PaymentComplementReport";
import { errorNotification } from "../../../utils/notifications";
import s from "./styles.scss";

interface Props {
  report: PaymentComplementReportRow[];
  searching: boolean;
  getPaymentComplementReport: (filters: PaymentComplementReportFilters) => void;
  toggleBill: (storageId: number) => void;
}

interface State {
  filters: PaymentComplementReportFilters;
}

const START_DATE_FIELDNAME = "fechaInicio";
const END_DATE_FIELDNAME = "fechaFin";

/**
 * Pantalla de reporte de complementos de pago.
 * Consume el BFF: GET /api/billing/payment-complements/reporte
 * (mismo flujo que el resto de pantallas de facturación).
 */
class PaymentComplementReport extends React.Component<Props, State> {
  state = {
    filters: {
      fechaInicio: null,
      fechaFin: null,
      referenceFolio: "",
      receiverRfc: ""
    }
  };

  onChangeFilter = fieldName => {
    return event => {
      const value = event.target.value;
      this.setState(state => ({
        ...state,
        filters: {
          ...state.filters,
          [fieldName]: value
        }
      }));
    };
  };

  onChangeDate = fieldName => {
    return event => {
      const date = event ? moment(event).format("YYYY-MM-DD") : null;
      this.setState(state => ({
        ...state,
        filters: {
          ...state.filters,
          [fieldName]: date
        }
      }));
    };
  };

  /**
   * Rango de fechas completo y válido: ambas fechas, fin >= inicio,
   * y la diferencia no mayor a un mes.
   */
  hasValidDateRange = (filters = this.state.filters) => {
    const { fechaInicio, fechaFin } = filters;
    if (!fechaInicio || !fechaFin) {
      return false;
    }
    const start = moment(fechaInicio, "YYYY-MM-DD", true);
    const end = moment(fechaFin, "YYYY-MM-DD", true);
    if (!start.isValid() || !end.isValid()) {
      return false;
    }
    if (end.isBefore(start, "day")) {
      return false;
    }
    // No mayor a un mes: fecha fin <= fecha inicio + 1 mes
    if (end.isAfter(start.clone().add(1, "month"), "day")) {
      return false;
    }
    return true;
  };

  /**
   * Indica si el rango de fechas está incompleto o es inválido (> 1 mes / fin < inicio).
   */
  getDateRangeError = (filters = this.state.filters) => {
    const { fechaInicio, fechaFin } = filters;
    if (!fechaInicio && !fechaFin) {
      return null;
    }
    if (!fechaInicio || !fechaFin) {
      return "Debe indicar fecha inicio y fecha fin";
    }
    const start = moment(fechaInicio, "YYYY-MM-DD", true);
    const end = moment(fechaFin, "YYYY-MM-DD", true);
    if (!start.isValid() || !end.isValid()) {
      return "Las fechas no son válidas";
    }
    if (end.isBefore(start, "day")) {
      return "La fecha fin no puede ser menor a la fecha inicio";
    }
    if (end.isAfter(start.clone().add(1, "month"), "day")) {
      return "El rango de fechas no puede ser mayor a un mes";
    }
    return null;
  };

  /**
   * Botones habilitados si hay al menos: rango de fechas válido,
   * folio de referencia o RFC receptor. Si hay fechas, deben ser válidas.
   */
  canSubmitSearch = (filters = this.state.filters) => {
    const dateError = this.getDateRangeError(filters);
    if (dateError) {
      return false;
    }
    const hasFolio = !!(filters.referenceFolio || "").trim();
    const hasRfc = !!(filters.receiverRfc || "").trim();
    return this.hasValidDateRange(filters) || hasFolio || hasRfc;
  };

  onSearch = e => {
    if (e) {
      e.preventDefault();
    }
    const dateError = this.getDateRangeError();
    if (dateError) {
      errorNotification(dateError);
      return;
    }
    if (!this.canSubmitSearch()) {
      errorNotification(
        "Indique un rango de fechas válido (máximo un mes), folio de referencia o RFC receptor"
      );
      return;
    }
    this.props.getPaymentComplementReport({ ...this.state.filters });
  };

  onClickDownloadCsv = () => {
    if (!this.canSubmitSearch()) {
      errorNotification(
        "Indique un rango de fechas válido (máximo un mes), folio de referencia o RFC receptor"
      );
      return;
    }
    // Solo las filas visibles en "Resultados de la búsqueda",
    // con los mismos valores/formato que la tabla.
    const tableRows = (this.props.report || []).map(row => ({
      "Fecha Emision": this.formatDate(row.emissionDate),
      "Fecha Certificacion Sat": this.formatDate(row.satCertificationDate),
      "Folio de la factura": this.formatInvoiceFolio(row),
      "Folio del complemento de pago": row.paymentComplementFolio || "",
      UUID: row.uuid || "",
      "RFC Emisor": row.issuerRfc || "",
      "RFC Receptor": row.receiverRfc || "",
      "Nombre Receptor": row.receiverName || "",
      "Uso CFDI": row.cfdiUsage || "",
      "Fecha Pago": this.formatDate(row.paymentDate),
      "Forma De Pago": row.paymentForm || "",
      Moneda: row.currency || "",
      "Monto del pago": row.amount != null ? row.amount : "",
      "Monto total de la factura":
        row.invoiceAmount != null ? row.invoiceAmount : "",
      "UUID Relacionado": row.relatedUuid || ""
    }));
    downloadPaymentComplementReportCsv(tableRows);
  };

  formatDate = value => {
    if (!value) {
      return "";
    }
    // LocalDateTime puede venir como ISO string o como arreglo [y,m,d,h,m,s]
    if (Array.isArray(value)) {
      const [y, m, d, hh = 0, mm = 0, ss = 0] = value;
      return moment({
        year: y,
        month: m - 1,
        day: d,
        hour: hh,
        minute: mm,
        second: ss
      }).format("DD/MM/YYYY HH:mm");
    }
    return moment(value).isValid()
      ? moment(value).format("DD/MM/YYYY HH:mm")
      : String(value);
  };

  /** Une serie + folio de la factura (ej. BN99548). */
  formatInvoiceFolio = (row: PaymentComplementReportRow) => {
    const series = (row.series || "").trim();
    const folio = (row.folio || "").trim();
    if (series && folio) {
      return `${series}${folio}`;
    }
    return series || folio || "";
  };

  renderHeader = (line1: string, line2?: string) => (
    <p className={`font-weight-bold ${s.headerCell}`}>
      {line1}
      {line2 ? (
        <>
          <br />
          {line2}
        </>
      ) : null}
    </p>
  );

  columns = [
    {
      Header: () => this.renderHeader("Fecha Emision"),
      id: "emissionDate",
      width: 110,
      accessor: "emissionDate",
      Cell: row => (
        <p className={s.initial}>
          {this.formatDate(row.original.emissionDate)}
        </p>
      )
    },
    {
      Header: () => this.renderHeader("Fecha Certificacion", "Sat"),
      id: "satCertificationDate",
      width: 120,
      accessor: "satCertificationDate",
      Cell: row => (
        <p className={s.initial}>
          {this.formatDate(row.original.satCertificationDate)}
        </p>
      )
    },
    {
      Header: () => this.renderHeader("Folio de la", "factura"),
      id: "invoiceFolio",
      width: 110,
      accessor: "folio",
      Cell: row => (
        <p className={s.initial}>{this.formatInvoiceFolio(row.original)}</p>
      )
    },
    {
      Header: () => this.renderHeader("Folio del complemento", "de pago"),
      id: "paymentComplementFolio",
      width: 140,
      accessor: "paymentComplementFolio",
      Cell: row => (
        <p className={s.initial}>{row.original.paymentComplementFolio || ""}</p>
      )
    },
    {
      Header: () => this.renderHeader("UUID"),
      id: "uuid",
      width: 220,
      accessor: "uuid",
      Cell: row => <p className={s.initial}>{row.original.uuid || ""}</p>
    },
    {
      Header: () => this.renderHeader("RFC Emisor"),
      id: "issuerRfc",
      width: 120,
      accessor: "issuerRfc",
      Cell: row => <p className={s.initial}>{row.original.issuerRfc || ""}</p>
    },
    {
      Header: () => this.renderHeader("RFC Receptor"),
      id: "receiverRfc",
      width: 120,
      accessor: "receiverRfc",
      Cell: row => <p className={s.initial}>{row.original.receiverRfc || ""}</p>
    },
    {
      Header: () => this.renderHeader("Nombre Receptor"),
      id: "receiverName",
      width: 200,
      accessor: "receiverName",
      Cell: row => (
        <p className={s.initial}>{row.original.receiverName || ""}</p>
      )
    },
    {
      Header: () => this.renderHeader("Uso CFDI"),
      id: "cfdiUsage",
      width: 90,
      accessor: "cfdiUsage",
      Cell: row => <p className={s.initial}>{row.original.cfdiUsage || ""}</p>
    },
    {
      Header: () => this.renderHeader("Fecha Pago"),
      id: "paymentDate",
      width: 120,
      accessor: "paymentDate",
      Cell: row => (
        <p className={s.initial}>{this.formatDate(row.original.paymentDate)}</p>
      )
    },
    {
      Header: () => this.renderHeader("Forma De", "Pago"),
      id: "paymentForm",
      width: 100,
      accessor: "paymentForm",
      Cell: row => <p className={s.initial}>{row.original.paymentForm || ""}</p>
    },
    {
      Header: () => this.renderHeader("Moneda"),
      id: "currency",
      width: 80,
      accessor: "currency",
      Cell: row => <p className={s.initial}>{row.original.currency || ""}</p>
    },
    {
      Header: () => this.renderHeader("Monto del", "pago"),
      id: "amount",
      width: 110,
      accessor: "amount",
      Cell: row => (
        <p className={s.initial}>
          <MexicanCurrency quantity={row.original.amount || 0} />
        </p>
      )
    },
    {
      Header: () => this.renderHeader("Monto total de", "la factura"),
      id: "invoiceAmount",
      width: 120,
      accessor: "invoiceAmount",
      Cell: row => (
        <p className={s.initial}>
          <MexicanCurrency quantity={row.original.invoiceAmount || 0} />
        </p>
      )
    },
    {
      Header: () => this.renderHeader("UUID", "Relacionado"),
      id: "relatedUuid",
      width: 220,
      accessor: "relatedUuid",
      Cell: row => <p className={s.initial}>{row.original.relatedUuid || ""}</p>
    },
    {
      Header: () => this.renderHeader("Opciones"),
      id: "actions",
      width: 100,
      accessor: "actions",
      Cell: row => {
        const pdfStorageId = row.original.pdfStorageId;
        const uuid = row.original.uuid;
        return (
          <Container className="p-0">
            <Row className="m-0">
              <BaseButtonWithIconAndTooltip
                id={`pdf${uuid || row.index}`}
                icon={faFilePdf}
                size="sm"
                tooltipTitle="Abrir Pdf"
                onClick={() => this.props.toggleBill(Number(pdfStorageId || 0))}
              />
              <BaseButtonWithIconAndTooltip
                id={`xml${uuid || row.index}`}
                icon={faFileAlt}
                size="sm"
                tooltipTitle="Abrir xml"
                onClick={() => openPaymentComplementReportXml(uuid)}
              />
            </Row>
          </Container>
        );
      }
    }
  ];

  render() {
    const { filters } = this.state;
    const hasResults = !!(this.props.report || []).length;
    const canSubmit = this.canSubmitSearch();
    const dateRangeError = this.getDateRangeError();

    return (
      <Container className="p-0 mt-3">
        <Form onSubmit={this.onSearch}>
          <div className="row">
            <div className="col">
              <p className={`${s.primaryTitle} w-100 m-0`}>
                Reporte complementos de pagos
              </p>
            </div>
          </div>

          <Row className="mt-3">
            <Col xs={12} md={3} lg={2}>
              <DatePicker
                label="Fecha inicio"
                name={START_DATE_FIELDNAME}
                id={START_DATE_FIELDNAME}
                selected={filters.fechaInicio}
                onChange={this.onChangeDate(START_DATE_FIELDNAME)}
              />
            </Col>
            <Col xs={12} md={3} lg={2}>
              <DatePicker
                label="Fecha fin"
                name={END_DATE_FIELDNAME}
                id={END_DATE_FIELDNAME}
                selected={filters.fechaFin}
                onChange={this.onChangeDate(END_DATE_FIELDNAME)}
              />
            </Col>
            <Col xs={12} md={3} lg={3}>
              <BaseInput
                label="Folio referencia"
                name="referenceFolio"
                id="referenceFolio"
                type="text"
                value={filters.referenceFolio}
                onChange={this.onChangeFilter("referenceFolio")}
              />
            </Col>
            <Col xs={12} md={3} lg={3}>
              <BaseInput
                label="RFC receptor"
                name="receiverRfc"
                id="receiverRfc"
                type="text"
                value={filters.receiverRfc}
                onChange={this.onChangeFilter("receiverRfc")}
              />
            </Col>
          </Row>
          {!!dateRangeError && (
            <Row>
              <Col xs={12}>
                <p
                  className="text-danger mb-0 mt-2"
                  style={{ fontSize: "0.85rem" }}
                >
                  {dateRangeError}
                </p>
              </Col>
            </Row>
          )}
          <Row>
            <Col xs={12} md={3} lg={3} className="mt-4">
              <Button
                type="submit"
                className={`${s.searchButton} ${s.primaryButton} btn-block`}
                disabled={this.props.searching || !canSubmit}
              >
                {this.props.searching ? "Buscando..." : "Buscar"}
              </Button>
            </Col>
            <Col xs={12} md={3} lg={3} className="mt-4">
              <Button
                type="button"
                className={`${s.searchButton} ${s.primaryButton} btn-block`}
                onClick={this.onClickDownloadCsv}
                disabled={this.props.searching || !canSubmit || !hasResults}
              >
                Descargar reporte
              </Button>
            </Col>
          </Row>
        </Form>

        {hasResults && (
          <Row className="mt-4">
            <Col sm="6">
              Resultados de la búsqueda:{" "}
              <b style={{ fontWeight: "bolder" }}>
                {(this.props.report || []).length}
              </b>
            </Col>
          </Row>
        )}

        <ReactTable
          data={this.props.report || []}
          columns={this.columns}
          className={`-striped -highlight mt-4 ${s.reportTable}`}
          pageSize={(this.props.report || []).length > 10 ? 10 : 5}
          showPagination
          sortable
          loading={this.props.searching}
          defaultPageSize={10}
        />
      </Container>
    );
  }
}

export default withRouter(withStyles(s)(PaymentComplementReport));
